<script>
  import UnifiedQuestionEditor from '../questionType/UnifiedQuestionEditor.svelte';
  import FieldLabelsEditor from '../common/FieldLabelsEditor.svelte';
  import ManyToManyEditor from '../common/ManyToManyEditor.svelte';
  import EncryptionSettings from '../common/EncryptionSettings.svelte';
  import { getRelationTypeFromDataset } from '../../../stores/questionsetStore';

  export let question;
  export let updateWorkingQuestion;
  export let hasUnsavedChanges;

  // Remove the problematic local variables and binding attempts
  // Use reactive statements for derived state only
  $: relationshipType = getRelationTypeFromDataset(question.dataset);

  // Validation functions - these are pure functions, no state needed
  function canQuestionTypeBeRelationship(questionType, relationshipType) {
    const rules = {
      'selfNode': ['Text', 'CheckBox', 'Select'],
      'selfProps': ['Text', 'CheckBox', 'Select', 'Quant', 'Map', 'DateTime'],
      'addProps': ['Text', 'CheckBox', 'Select', 'Quant', 'Map', 'DateTime'],  
      'createNodes': ['Text', 'CheckBox', 'Select']
    };

    return rules[relationshipType]?.includes(questionType) || false;
  }

  function getValidationMessage(questionType, relationshipType) {
    if (canQuestionTypeBeRelationship(questionType, relationshipType)) {
      return null;
    }

    const typeRules = {
      'selfNode': 'Self node questions can only be Text, CheckBox, or Select',
      'selfProps': null,
      'addProps': null,
      'createNodes': 'Node creation questions can only be Text, CheckBox, or Select'
    };

    return typeRules[relationshipType] || null;
  }

  // Reactive derived state - automatically updates when question changes
  $: validationMessage = getValidationMessage(question.kind, relationshipType);
  
  $: showFieldLabels = (() => {
    const hasNodes = question.dataset?.some(handler => handler[0] === 'nodes');
    const isText = question.kind === 'Text';
    const isSelect = question.kind === 'Select';
    const isMap = question.kind === 'Map';
    const isDateTime = question.kind === 'DateTime';
    const isQuant = question.kind === 'Quant';
    const isManyToMany = question.manyToMany === true;

    return (hasNodes && (isText || isSelect)) || isManyToMany ||
           (isMap && question.manyToMany) ||
           (isDateTime && question.fieldLabels) ||
           (isQuant && question.questionLabels?.length > 1);
  })();

  $: showManyToMany = (() => {
    const hasNodes = question.dataset?.some(handler => handler[0] === 'nodes');
    const hasSelfNode = question.dataset?.some(handler => handler[0] === 'setSelf');
    const hasSelfProps = question.dataset?.some(handler => handler[0] === 'selfProps');
    const hasNodeProps = question.dataset?.some(handler => handler[0] === 'nodeProps');

    return (hasNodes || hasSelfProps || hasNodeProps) && !hasSelfNode;
  })();

  $: showEncryption = true; // Always show encryption settings

  // Simple wrapper function to ensure hasUnsavedChanges is updated
  function handleUpdate(field, value) {
    updateWorkingQuestion(field, value);
    hasUnsavedChanges = true;
  }
</script>

<div class="question-type-settings">

  <!-- Validation Warning -->
  {#if validationMessage}
    <div class="validation-warning">
      <span class="warning-icon">⚠️</span>
      <span class="warning-text">{validationMessage}</span>
    </div>
  {/if}

  <!-- Unified Question Type Editor -->
  <UnifiedQuestionEditor 
    {question}
    updateWorkingQuestion={handleUpdate}
  />

  <!-- Enhanced Field Labels Configuration -->
  {#if showFieldLabels}
    <FieldLabelsEditor 
      {question}
      updateWorkingQuestion={handleUpdate}
    />
  {/if}

  <!-- Many-to-Many Configuration -->
  {#if showManyToMany}
    <ManyToManyEditor 
      {question}
      updateWorkingQuestion={handleUpdate}
    />
  {/if}

  <!-- Encryption Settings -->
  {#if showEncryption}
    <EncryptionSettings 
      {question}
      updateWorkingQuestion={handleUpdate}
    />
  {/if}
  
</div>

<style lang="scss">
  .question-type-settings {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .validation-warning {
    background: #fef3c7;
    border: 2px solid #f59e0b;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;

    .warning-icon {
      font-size: 1.2rem;
      flex-shrink: 0;
    }

    .warning-text {
      font-size: 0.8rem;
      color: #92400e;
      font-weight: 500;
    }
  }
</style>